import { Box, Link, Paper, TextField, Typography } from '@mui/material';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import type { IBubble } from '../../../shared/types';
import { getUserColor, parseTextWithUrls } from '../utils/bubbleUtils';

interface LiveBubbleProps {
  bubble: IBubble;
  currentUserId: string;
  onClaim: (bubbleId: string) => void;
  onTextChange: (bubbleId: string, text: string) => void;
  onFinalize: (bubbleId: string) => void;
}

const LiveBubble: React.FC<LiveBubbleProps> = ({
  bubble,
  currentUserId,
  onClaim,
  onTextChange,
  onFinalize,
}) => {
  const [localText, setLocalText] = useState(bubble.text);
  const textFieldRef = useRef<HTMLInputElement>(null);

  const isOwnBubble = bubble.ownerId === currentUserId;
  const isEmpty = !bubble.ownerId;
  const isFinalized = bubble.isFinalized;

  useEffect(() => {
    setLocalText(bubble.text);
  }, [bubble.text]);

  const handleClick = () => {
    if (isEmpty) {
      onClaim(bubble.id);
    }
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newText = event.target.value;
    setLocalText(newText);
    if (isOwnBubble) {
      onTextChange(bubble.id, newText);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && isOwnBubble && !isFinalized) {
      onFinalize(bubble.id);
      event.preventDefault();
    }
  };

  const getBubbleColor = () => {
    if (isEmpty) return '#2a2a2a';
    if (isOwnBubble) {
      return isFinalized ? '#1565c0' : '#1976d2';
    }
    // Use random color for other users
    return getUserColor(bubble.ownerName);
  };

  const getTextColor = () => {
    if (isEmpty) return '#666';
    return '#fff';
  };

  const getBorderStyle = () => {
    if (isEmpty) return '2px dashed #666';
    if (!isFinalized && isOwnBubble) return '2px solid #1976d2';
    return 'none';
  };

  const renderTextContent = (text: string) => {
    if (!isFinalized) {
      return text;
    }

    const parts = parseTextWithUrls(text);

    return parts.map((part, index) => {
      if (typeof part === 'string') {
        return part;
      } else {
        return (
          <Link
            key={`url-${part.url}-${index}`}
            href={part.url}
            target='_blank'
            rel='noopener noreferrer'
            sx={{
              color: 'inherit',
              textDecoration: 'underline',
              '&:hover': {
                textDecoration: 'underline',
                opacity: 0.8,
              },
            }}
          >
            {part.display}
          </Link>
        );
      }
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isOwnBubble || isEmpty ? 'flex-end' : 'flex-start',
        marginBottom: 1,
        width: '100%',
      }}
    >
      <Paper
        elevation={isFinalized ? 3 : 2}
        onClick={handleClick}
        sx={{
          padding: 2,
          backgroundColor: getBubbleColor(),
          cursor: isEmpty ? 'pointer' : 'default',
          border: getBorderStyle(),
          minHeight: '60px',
          maxWidth: { xs: '85%', sm: '75%', md: '65%' },
          minWidth: '200px',
          transition: 'all 0.2s ease',
          opacity: isFinalized ? 0.9 : 1,
          borderRadius: isOwnBubble || isEmpty ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
          '&:hover': {
            backgroundColor: isEmpty ? '#333' : getBubbleColor(),
          },
        }}
      >
        {!isEmpty && (
          <Typography variant='caption' sx={{ color: '#bbb', marginBottom: 1, display: 'block' }}>
            {bubble.ownerName}
            {!isFinalized && ' (editing...)'}
            {bubble.claimedAt && (
              <span style={{ marginLeft: '8px', opacity: 0.7 }}>
                {new Date(bubble.claimedAt).toLocaleTimeString()}
              </span>
            )}
          </Typography>
        )}

        {isOwnBubble && !isFinalized ? (
          <TextField
            ref={textFieldRef}
            value={localText}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder='Type your message...'
            variant='standard'
            fullWidth
            multiline
            autoFocus
            InputProps={{
              disableUnderline: true,
              style: { color: getTextColor(), fontSize: '16px' },
            }}
            sx={{
              '& .MuiInputBase-input': {
                padding: 0,
              },
            }}
          />
        ) : (
          <Box sx={{ minHeight: '24px' }}>
            {isEmpty ? (
              <Typography variant='body2' sx={{ color: '#666', fontStyle: 'italic' }}>
                Type here...
              </Typography>
            ) : (
              <Typography
                variant='body1'
                sx={{
                  color: getTextColor(),
                  whiteSpace: 'pre-wrap',
                  cursor: 'default',
                  userSelect: 'text',
                  '&::after':
                    !isFinalized && localText && !isOwnBubble
                      ? {
                          content: '"|"',
                          opacity: 0.6,
                          animation: 'blink 1s infinite',
                          '@keyframes blink': {
                            '0%, 50%': { opacity: 0.6 },
                            '51%, 100%': { opacity: 0 },
                          },
                        }
                      : undefined,
                }}
              >
                {renderTextContent(localText)}
                {!isFinalized && localText && isOwnBubble && (
                  <span style={{ opacity: 0.6 }}>|</span>
                )}
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default LiveBubble;
